import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import MerchantLayout from '@/components/layouts/MerchantLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Copy, Download, Code, Globe, Webhook, ArrowRight,
  Link2, Shield, FileCode, CheckCircle, ExternalLink,
  ChevronDown, ChevronRight, Zap, Terminal, FileJson
} from 'lucide-react';

const CodeBlock = ({ code, filename, onCopy, onDownload }) => (
  <div className="relative group">
    <div className="flex items-center justify-between bg-muted px-4 py-2.5 rounded-t-xl border border-b-0 border-gray-200 dark:border-white/10">
      <div className="flex items-center gap-2">
        <Terminal className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-mono text-muted-foreground">{filename}</span>
      </div>
      <div className="flex gap-1.5">
        <button
          onClick={() => onCopy(code)}
          className="px-2.5 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors font-medium"
        >
          <Copy className="h-3 w-3 inline mr-1" />Copy
        </button>
        {onDownload && (
          <button
            onClick={() => onDownload(filename, code)}
            className="px-2.5 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors font-medium"
          >
            <Download className="h-3 w-3 inline mr-1" />Download
          </button>
        )}
      </div>
    </div>
    <pre className="p-4 bg-background rounded-b-xl border border-t-0 border-gray-200 dark:border-white/10 overflow-x-auto max-h-[380px] scrollbar-thin">
      <code className="text-[13px] font-mono text-muted-foreground leading-relaxed">{code}</code>
    </pre>
  </div>
);

const IntegrationCard = ({ icon: Icon, title, subtitle, tag, isNew, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-2xl overflow-hidden bg-card">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 px-6 py-5 hover:bg-muted transition-colors text-left"
        data-testid={`integration-card-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="w-10 h-10 rounded-xl bg-muted border border-border flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            {tag && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground uppercase tracking-wider">{tag}</span>
            )}
            {isNew && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/10 text-emerald-500 uppercase tracking-wider">New</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-border">
          <div className="pt-5">{children}</div>
        </div>
      )}
    </div>
  );
};

const MerchantIntegrations = () => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('html');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [keysRes, walletsRes] = await Promise.all([
        axios.get(`${API}/api-keys`),
        axios.get(`${API}/wallets`)
      ]);
      setApiKeys(keysRes.data);
      setWallets(walletsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard');
  };

  const downloadFile = (filename, content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  const apiKey = apiKeys.length > 0 ? 'YOUR_API_KEY' : 'Generate an API key first';
  const walletId = wallets[0]?.id || 'YOUR_WALLET_ID';
  const baseUrl = API.replace('/api', '');

  const hostedCheckoutFlow = `# Hosted Checkout Flow

1. Merchant creates invoice via API:
   POST ${API}/invoices
   {
     "amount": 100,
     "currency": "USDT",
     "description": "Order #1234"
   }

2. Redirect customer to checkout:
   ${baseUrl}/checkout/{invoice_id}

3. Customer pays via hosted page

4. CoinPayUp verifies payment on-chain

5. Merchant receives confirmation via:
   - Redirect: success_url?invoice_id={id}&status=paid
   - Email notification
   - Dashboard update
   - Webhook (optional)`;

  const phpCreateInvoice = `<?php
// CoinPayUp — Create Invoice (save as create-invoice.php)
$API_KEY = '${apiKey}';
$API_URL = '${API}';
$CHECKOUT = '${baseUrl}';

$input = json_decode(file_get_contents('php://input'), true);
$data = [
    'amount' => (float)($input['amount'] ?? 100),
    'currency' => $input['currency'] ?? 'USDT',
    'description' => $input['description'] ?? 'Payment',
    'expiry_minutes' => 30
];

$ch = curl_init();
curl_setopt_array($ch, [
    CURLOPT_URL => $API_URL . '/invoices',
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . $API_KEY,
        'Content-Type: application/json'
    ],
    CURLOPT_POSTFIELDS => json_encode($data)
]);

$response = curl_exec($ch);
$invoice = json_decode($response, true);

if (isset($invoice['id'])) {
    echo json_encode([
        'success' => true,
        'checkout_url' => $CHECKOUT . '/checkout/' . $invoice['id']
    ]);
} else {
    http_response_code(400);
    echo json_encode(['success' => false, 'error' => $invoice['detail'] ?? 'Failed']);
}
?>`;

  const phpWebhook = `<?php
// CoinPayUp — Webhook Handler (save as webhook.php)
$SECRET = 'your_webhook_secret_here';

$payload = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_COINPAYUP_SIGNATURE'] ?? '';
$event = $_SERVER['HTTP_X_COINPAYUP_EVENT'] ?? '';
$deliveryId = $_SERVER['HTTP_X_COINPAYUP_DELIVERY_ID'] ?? '';

// Verify HMAC SHA256 signature
$expected = hash_hmac('sha256', $payload, $SECRET);
if (!hash_equals($expected, $signature)) {
    http_response_code(401);
    die('Invalid signature');
}

$data = json_decode($payload, true);

switch ($event) {
    case 'payment.confirmed':
        // Payment confirmed on blockchain
        $invoiceId = $data['data']['invoice_id'];
        $amount = $data['data']['amount'];
        $txHash = $data['data']['tx_hash'];
        // TODO: Update your order status
        break;
    case 'invoice.paid':
        // Invoice marked as paid
        break;
    case 'invoice.expired':
        // Invoice expired without payment
        break;
    case 'payment.detected':
        // Payment detected (not yet confirmed)
        break;
}

http_response_code(200);
echo json_encode(['status' => 'ok']);
?>`;

  const jsSDKCode = `// CoinPayUp JavaScript SDK
class CoinPayUp {
  constructor(apiKey) {
    this.apiKey = apiKey;
    this.apiUrl = '${API}';
    this.checkoutUrl = '${baseUrl}/checkout';
  }

  async createInvoice({ amount, currency = 'USDT', description = '', expiryMinutes = 30 }) {
    const resp = await fetch(this.apiUrl + '/invoices', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + this.apiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ amount: parseFloat(amount), currency, description, expiry_minutes: expiryMinutes })
    });
    if (!resp.ok) throw new Error((await resp.json()).detail || 'Failed');
    const invoice = await resp.json();
    return { ...invoice, checkoutUrl: this.checkoutUrl + '/' + invoice.id };
  }

  async getStatus(invoiceId) {
    const resp = await fetch(this.apiUrl + '/invoices/' + invoiceId + '/status');
    return resp.json();
  }

  redirectToCheckout(invoiceId) {
    window.location.href = this.checkoutUrl + '/' + invoiceId;
  }
}

// Usage:
// const cp = new CoinPayUp('cpk_your_api_key');
// const inv = await cp.createInvoice({ amount: 100, currency: 'USDT' });
// cp.redirectToCheckout(inv.id);`;

  const htmlPaymentButton = `<!-- CoinPayUp Payment Button -->
<button onclick="payWithCrypto()" style="
  background:#10B981; color:#000; padding:12px 28px;
  border:none; border-radius:8px; font-size:15px;
  font-weight:600; cursor:pointer;">
  Pay with Crypto
</button>

<script>
async function payWithCrypto() {
  const resp = await fetch('/create-invoice.php', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount: 100, currency: 'USDT', description: 'Order #1234' })
  });
  const data = await resp.json();
  if (data.checkout_url) window.location.href = data.checkout_url;
  else alert('Error: ' + (data.error || 'Payment failed'));
}
</script>`;

  const statusPollingCode = `// Poll invoice status (webhook alternative)
async function pollStatus(invoiceId) {
  const resp = await fetch('${API}/invoices/' + invoiceId + '/status');
  const data = await resp.json();
  // { status: "paid", confirmations: 12, tx_hash: "0x...", amount: 100, currency: "USDT" }
  
  if (data.status === 'paid') {
    console.log('Payment confirmed!', data);
  } else if (data.status === 'pending') {
    setTimeout(() => pollStatus(invoiceId), 5000); // Poll every 5s
  }
}`;

  const tabs = [
    { id: 'html', label: 'HTML', icon: Globe },
    { id: 'php', label: 'PHP', icon: FileCode },
    { id: 'webhook', label: 'Webhook', icon: Webhook },
    { id: 'js', label: 'JavaScript', icon: Code },
    { id: 'polling', label: 'Status API', icon: Zap },
  ];

  const apiEndpoints = [
    { method: 'POST', path: '/api/invoices', desc: 'Create invoice' },
    { method: 'GET', path: '/api/invoices/{id}', desc: 'Get invoice details' },
    { method: 'GET', path: '/api/invoices/{id}/status', desc: 'Poll payment status' },
    { method: 'GET', path: '/api/invoices', desc: 'List invoices' },
    { method: 'POST', path: '/api/payment-links', desc: 'Create payment link' },
    { method: 'GET', path: '/api/payment-links', desc: 'List payment links' },
    { method: 'GET', path: '/api/wallets', desc: 'List wallets' },
    { method: 'POST', path: '/api/wallets', desc: 'Add wallet' },
    { method: 'POST', path: '/api/webhooks', desc: 'Add webhook endpoint' },
    { method: 'GET', path: '/api/webhooks/logs', desc: 'Webhook delivery logs' },
  ];

  return (
    <MerchantLayout title="Integrations">
      <div className="space-y-6 max-w-4xl" data-testid="integrations-page">

        {/* Credentials Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'API Endpoint', value: API },
            { label: 'Checkout URL', value: `${baseUrl}/checkout/{id}` },
            { label: 'API Keys', value: apiKeys.length > 0 ? `${apiKeys.length} active` : 'None' },
            { label: 'Wallets', value: wallets.length > 0 ? `${wallets.length} active` : 'None' },
          ].map((item) => (
            <div key={item.label} className="p-3 bg-card border border-border rounded-xl">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{item.label}</p>
              <p className="text-xs font-mono text-foreground truncate">{item.value}</p>
            </div>
          ))}
        </div>

        {/* Integration Modes */}
        <div>
          <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3 px-1">Integration Modes</h2>
          <div className="space-y-3">
            <IntegrationCard
              icon={Globe}
              title="Hosted Checkout"
              subtitle="Redirect customers to a secure hosted payment page"
              tag="Default"
              defaultOpen={true}
            >
              <div className="space-y-4">
                <div className="flex items-start gap-3 flex-wrap">
                  {['Create Invoice via API', 'Redirect to Checkout', 'Customer Pays', 'Payment Verified', 'Merchant Notified'].map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold text-emerald-400">{i + 1}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{step}</span>
                      {i < 4 && <ArrowRight className="h-3 w-3 text-muted-foreground/40" />}
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-muted border-l-2 border-emerald-500/30 rounded-r-lg">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    After payment, customers are redirected to your <code className="text-emerald-500 dark:text-emerald-400 font-mono">success_url</code> with <code className="text-emerald-500 dark:text-emerald-400 font-mono">?invoice_id=&#123;id&#125;&status=paid</code>. Configure redirect URLs in Payment Notifications settings.
                  </p>
                </div>
                <CodeBlock code={hostedCheckoutFlow} filename="flow.txt" onCopy={copyCode} />
              </div>
            </IntegrationCard>

            <IntegrationCard
              icon={Link2}
              title="Payment Links"
              subtitle="Generate shareable payment links — no coding required"
              tag="No-Code"
              isNew
            >
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Create one-click payment links to share via email, social media, or messaging. Each link has a fixed amount, description, and expiry timer.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {['Fixed amount', 'Product description', 'Expiry timer', 'Merchant branding'].map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-emerald-500" />
                      <span className="text-xs text-muted-foreground">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-muted border border-gray-200 dark:border-white/10 rounded-xl">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Link Format</p>
                  <code className="text-xs font-mono text-emerald-500 dark:text-emerald-400">{baseUrl}/p/&#123;payment_slug&#125;</code>
                </div>
                <p className="text-xs text-muted-foreground">
                  Manage payment links from the{' '}
                  <a href="/webhooks" className="text-emerald-500 dark:text-emerald-400 hover:underline transition-colors">Payment Notifications</a> page.
                </p>
              </div>
            </IntegrationCard>

            <IntegrationCard
              icon={Webhook}
              title="Auto-Managed Webhooks"
              subtitle="CoinPayUp hosts and manages webhook delivery automatically"
              isNew
            >
              <div className="space-y-4">
                <div className="grid gap-3">
                  {[
                    { level: 'Beginner', desc: 'Redirect after payment + email + dashboard updates', color: 'emerald' },
                    { level: 'Standard', desc: 'Auto-hosted webhook relay for Zapier, Make.com, no-code tools', color: 'blue' },
                    { level: 'Advanced', desc: 'Custom webhook endpoint with HMAC SHA256 signing', color: 'amber' },
                  ].map((tier) => (
                    <div key={tier.level} className="flex items-start gap-3 p-3 bg-background rounded-xl border border-gray-200 dark:border-white/10">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                        tier.color === 'emerald' ? 'bg-emerald-500' : tier.color === 'blue' ? 'bg-blue-500' : 'bg-amber-500'
                      }`} />
                      <div>
                        <p className="text-xs font-medium text-foreground">{tier.level}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{tier.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Configure notification preferences in{' '}
                  <a href="/webhooks" className="text-emerald-500 dark:text-emerald-400 hover:underline transition-colors">Payment Notifications</a>.
                </p>
              </div>
            </IntegrationCard>
          </div>
        </div>

        {/* Developer Tools */}
        <div>
          <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3 px-1">Developer Tools</h2>
          <div className="space-y-3">
            <IntegrationCard
              icon={FileJson}
              title="Postman Collection"
              subtitle="Download a ready-to-use Postman collection with all API endpoints"
              isNew
            >
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Import this collection into Postman to instantly test every CoinPayUp API endpoint. The collection includes pre-configured variables for your API base URL and key.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['Auth & Users', 'Invoices & Payments', 'Wallets & Links', 'Webhooks & Keys'].map((g) => (
                    <div key={g} className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0" />
                      <span className="text-xs text-muted-foreground">{g}</span>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-muted border-l-2 border-orange-500/30 rounded-r-lg">
                  <p className="text-xs text-muted-foreground">
                    After importing, set the <code className="font-mono text-orange-500 dark:text-orange-400">baseUrl</code> and <code className="font-mono text-orange-500 dark:text-orange-400">apiKey</code> variables in Postman's collection settings.
                  </p>
                </div>
                <Button
                  data-testid="download-postman-collection"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/integrations/postman-collection`, { responseType: 'blob' });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'CoinPayUp.postman_collection.json';
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success('Postman collection downloaded');
                    } catch {
                      toast.error('Failed to download collection');
                    }
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Postman Collection
                </Button>
              </div>
            </IntegrationCard>
          </div>
        </div>

        {/* Code Snippets */}
        <div>
          <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3 px-1">Code Snippets</h2>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-border overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium transition-colors whitespace-nowrap border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-500 dark:border-emerald-400 dark:text-emerald-400'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-5">
              {activeTab === 'html' && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-1">Payment Button</h3>
                    <p className="text-xs text-muted-foreground mb-3">Drop-in button for your website</p>
                    <CodeBlock code={htmlPaymentButton} filename="payment-button.html" onCopy={copyCode} onDownload={downloadFile} />
                  </div>
                </div>
              )}
              {activeTab === 'php' && (
                <div className="space-y-4">
                  <div className="p-3 bg-amber-500/10 border-l-2 border-amber-500/30 rounded-r-lg">
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Replace <code className="font-mono text-amber-700 dark:text-amber-300">YOUR_API_KEY</code> with your actual API key.
                    </p>
                  </div>
                  <CodeBlock code={phpCreateInvoice} filename="create-invoice.php" onCopy={copyCode} onDownload={downloadFile} />
                </div>
              )}
              {activeTab === 'webhook' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-foreground mb-1">Webhook Handler</h3>
                  <p className="text-xs text-muted-foreground mb-3">Receive and verify payment notifications on your server</p>
                  <CodeBlock code={phpWebhook} filename="webhook.php" onCopy={copyCode} onDownload={downloadFile} />
                </div>
              )}
              {activeTab === 'js' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-foreground mb-1">JavaScript SDK</h3>
                  <p className="text-xs text-muted-foreground mb-3">Client-side library for invoice creation and checkout</p>
                  <CodeBlock code={jsSDKCode} filename="coinpayup-sdk.js" onCopy={copyCode} onDownload={downloadFile} />
                </div>
              )}
              {activeTab === 'polling' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-foreground mb-1">Payment Status API</h3>
                  <p className="text-xs text-muted-foreground mb-3">Lightweight polling alternative to webhooks</p>
                  <div className="p-3 bg-muted border-l-2 border-blue-500/30 rounded-r-lg mb-3">
                    <p className="text-xs text-muted-foreground">
                      <code className="font-mono text-blue-500 dark:text-blue-400">GET /api/invoices/&#123;id&#125;/status</code> — returns status, confirmations, tx_hash, and amount.
                    </p>
                  </div>
                  <CodeBlock code={statusPollingCode} filename="poll-status.js" onCopy={copyCode} onDownload={downloadFile} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* API Reference */}
        <div>
          <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3 px-1">API Reference</h2>
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="divide-y divide-border">
              {apiEndpoints.map((ep, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3 hover:bg-muted transition-colors" data-testid={`api-endpoint-${i}`}>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold tracking-wider ${
                    ep.method === 'POST' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-blue-500/10 text-blue-400'
                  }`}>
                    {ep.method}
                  </span>
                  <code className="text-xs font-mono text-muted-foreground flex-1">{ep.path}</code>
                  <span className="text-[11px] text-muted-foreground">{ep.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Webhook Events Reference */}
        <div>
          <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-3 px-1">Webhook Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { event: 'payment.detected', desc: 'Payment transaction detected on-chain (unconfirmed)', color: 'text-blue-500 dark:text-blue-400' },
              { event: 'payment.confirmed', desc: 'Payment reaches required block confirmations', color: 'text-emerald-500 dark:text-emerald-400' },
              { event: 'invoice.paid', desc: 'Invoice marked as paid', color: 'text-emerald-500 dark:text-emerald-400' },
              { event: 'invoice.expired', desc: 'Invoice expired without payment', color: 'text-amber-500 dark:text-amber-400' },
            ].map((evt) => (
              <div key={evt.event} className="p-3 bg-card border border-border rounded-xl">
                <code className={`text-xs font-mono ${evt.color}`}>{evt.event}</code>
                <p className="text-[11px] text-muted-foreground mt-1">{evt.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </MerchantLayout>
  );
};

export default MerchantIntegrations;
